/* eslint-disable @typescript-eslint/no-empty-function */
import type { ReactNode } from 'react';

import Head from 'next/head';
import { createContext, useContext, useMemo } from 'react';

import { Environments, PageTypes } from '../../constants';
import {
  AlgoliaHostMap,
  AmplienceMediaMap,
  CloudinaryImageMap1,
  CloudinaryImageMap2,
  ProductionHost,
} from '~/utilities/constants/siteType.config';
import { SiteType } from '~/utilities/graphql/codegen';

export type AppContextType = {
  /**
   * IETF / BCP 47 language tag separated by a `dash`
   *
   * @example 'en-nl'
   */
  bcp47Locale: string;
  /**
   * Browser locale format with uppercase country code
   *
   * @example 'nl-NL'
   */
  browserLocale: string;
  /**
   * Locale filtering rules https://amplience.com/developers/docs/dev-tools/guides-tutorials/localization/#locale-filtering-rules
   *
   * @example 'nl-NL,nl,en'
   */
  amplienceLocale: string;
  /**
   * POSIX langauge tag separated by an `underscore`
   *
   * @example 'en_nl'
   */
  locale: string;
  country: string;
  /**
   * lowercase language code
   *
   * @example 'nl'
   */
  language: string;
  /**
   * uppercase language code
   *
   * @example 'EN'
   */
  lang: string;
  /**
   * Flag indicating if the application is running in production mode or not
   *
   * uses `process.env.ENVIRONMENT` environment variable
   */
  isProduction: boolean;
  siteType: SiteType;
  cloudinaryURL1: string;
  cloudinaryURL2: string;
  amplienceMediaURL: string;
  algoliaCustomHost: string[];
  /**
   * hostname of the site when deployed to production
   */
  productionHost: string;
  isGStar: boolean;
  pageType: PageTypes;
};

const appContextDefaultValues: AppContextType = {
  bcp47Locale: '',
  browserLocale: '',
  locale: 'default',
  amplienceLocale: 'en',
  country: '',
  language: '',
  lang: '',
  isProduction: false,
  siteType: '' as SiteType,
  cloudinaryURL1: '',
  cloudinaryURL2: '',
  amplienceMediaURL: '',
  algoliaCustomHost: [],
  productionHost: '',
  isGStar: true,
  pageType: PageTypes.OTHER,
};

const GStarBrands = new Set([SiteType.Gstar, SiteType.Outlet, SiteType.Employeeshop]);

const isGStar = (siteType: SiteType) => GStarBrands.has(siteType);

const AppContext = createContext<AppContextType>(appContextDefaultValues);

export function useAppContext() {
  return useContext(AppContext);
}

type Props = {
  locale: string;
  children: ReactNode;
  siteType: SiteType;
  pageType: PageTypes;
};

const LOCALE_PATTERN = /([a-zA-Z]){2}_([a-zA-Z]){2}$/;

export function AppProvider({ locale, siteType, children, pageType }: Readonly<Props>) {
  const value = useMemo((): AppContextType => {
    const [language, country] = LOCALE_PATTERN.test(locale) ? locale.split('_') : ['', ''];
    const lang = language.toUpperCase();
    const isProduction = process.env.ENVIRONMENT === Environments.PRD;
    const bcp47Locale = locale.replace('_', '-');
    const browserLocale = `${language}-${country.toUpperCase()}`;
    const amplienceLocale = `${browserLocale},${language},en`;

    // TODO: this should be moved somewhere to configuration and read from configuration instead
    const cloudinaryURL1 = CloudinaryImageMap1[siteType];
    const cloudinaryURL2 = CloudinaryImageMap2[siteType];
    const amplienceMediaURL = AmplienceMediaMap[siteType];
    const algoliaCustomHost = AlgoliaHostMap[siteType];
    const productionHost = ProductionHost[siteType];

    return {
      locale,
      country,
      language,
      lang,
      isProduction,
      bcp47Locale,
      browserLocale,
      amplienceLocale,
      pageType,
      siteType,
      cloudinaryURL1,
      cloudinaryURL2,
      amplienceMediaURL,
      algoliaCustomHost,
      productionHost,
      isGStar: isGStar(siteType),
    } as AppContextType;
  }, [locale, siteType, pageType]);

  return (
    <AppContext.Provider value={value}>
      <Head>
        <link rel="preconnect" href={value.cloudinaryURL1} />
        <link rel="preconnect" href={value.cloudinaryURL2} />
        <link rel="preconnect" href={value.amplienceMediaURL} />
      </Head>

      {children}
    </AppContext.Provider>
  );
}
